import { useState } from "react";
import { useLanguage } from "./LanguageContext";
import { API_BASE_URL } from "../config";

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.45)",
  backdropFilter: "blur(2px)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 100,
};

const cardStyle = {
  background: "white",
  borderRadius: 14,
  boxShadow: "0 20px 48px rgba(0,0,0,0.12)",
  width: 440,
  maxWidth: "90vw",
  position: "relative",
  padding: "32px 28px",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: 24,
};

const titleStyle = {
  fontFamily: "'new-science','DM Sans',sans-serif",
  fontSize: 22,
  fontWeight: 700,
  color: "#0f172a",
  margin: 0,
};

const subtitleStyle = {
  fontSize: 14,
  color: "#64748b",
  margin: "8px 0 0 0",
};

const closeButtonStyle = {
  background: "none",
  border: "none",
  fontSize: 20,
  color: "#94a3b8",
  cursor: "pointer",
  padding: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 32,
  height: 32,
  marginLeft: 16,
};

const fieldStyle = {
  marginBottom: 16,
};

const labelStyle = {
  display: "block",
  fontSize: 13,
  fontWeight: 600,
  color: "#334155",
  marginBottom: 6,
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 6,
  border: "1px solid #e2e8f0",
  fontSize: 14,
  fontFamily: "'DM Sans', sans-serif",
  boxSizing: "border-box",
};

const submitButtonStyle = {
  width: "100%",
  padding: "11px 0",
  borderRadius: 8,
  border: "none",
  background: "#2563eb",
  color: "white",
  fontFamily: "'DM Sans',sans-serif",
  fontSize: 14,
  fontWeight: 700,
  cursor: "pointer",
  marginTop: 8,
};

const successContainerStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 16,
  minHeight: 200,
};

const successIconStyle = {
  fontSize: 48,
};

const successTextStyle = {
  fontSize: 15,
  color: "#334155",
  textAlign: "center",
  lineHeight: 1.6,
};

const errorStyle = {
  fontSize: 13,
  color: "#dc2626",
  marginTop: 8,
};

export default function InterestModal({ plan, onClose }) {
  const { t } = useLanguage();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!firstName.trim() || !email.trim()) {
      setError(t("common.required"));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim(),
          plan,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to register");
      }

      setSuccess(true);
      setTimeout(() => onClose(), 2000);
    } catch (err) {
      console.error("Waitlist error:", err);
      setError(err.message || "Error registering. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <div style={{ flex: 1 }}>
            <h2 style={titleStyle}>{t("waitlist.title")}</h2>
            <p style={subtitleStyle}>
              {t("waitlist.subtitle").replace("{plan}", plan)}
            </p>
          </div>
          <button style={closeButtonStyle} onClick={onClose}>
            ✕
          </button>
        </div>

        {success ? (
          <div style={successContainerStyle}>
            <div style={successIconStyle}>✓</div>
            <p style={successTextStyle}>{t("waitlist.success")}</p>
            <button style={submitButtonStyle} onClick={onClose}>
              {t("waitlist.close")}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={fieldStyle}>
              <label style={labelStyle}>{t("waitlist.firstName")} *</label>
              <input
                style={inputStyle}
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder=""
                disabled={isSubmitting}
              />
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>{t("waitlist.lastName")}</label>
              <input
                style={inputStyle}
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder=""
                disabled={isSubmitting}
              />
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>{t("waitlist.email")} *</label>
              <input
                style={inputStyle}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder=""
                disabled={isSubmitting}
              />
            </div>

            {error && <div style={errorStyle}>{error}</div>}

            <button
              type="submit"
              style={{
                ...submitButtonStyle,
                opacity: isSubmitting ? 0.7 : 1,
                cursor: isSubmitting ? "default" : "pointer",
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? t("waitlist.submitting") : t("waitlist.submit")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
